import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LIMITS } from '@bunker/shared/limits';
import { api } from '@/lib/api';
import { presenceLabel, presenceDescription, presenceDotClass } from '@/lib/presence';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { rosterCache } from '@/lib/rosterCache';
import { fileToIconDataUrl, ImageUploadError, dataUrlToBlob } from '@/lib/imageUpload';
import { avatarErrorMessage } from '@/lib/avatarErrors';
import { humanizeApiError } from '@/lib/humanizeApiError';
import { getPlatformUrl } from '@/lib/inviteLinks';
import { VoicePreferencesTab } from '@/components/VoicePreferencesTab';
import { NotificationsTab } from '@/components/NotificationsTab';
import { AppearanceTab } from '@/components/userSettings/AppearanceTab';
import { ShortcutsTab } from '@/components/userSettings/ShortcutsTab';
import { PrivacyTab } from '@/components/userSettings/PrivacyTab';
import { ProfileQrCard } from '@/components/userSettings/ProfileQrCard';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/Button';
import { ChevronLeftIcon, CloseIcon } from '@/components/icons';
import { DiscardChangesDialog } from '@/components/ui/DiscardChangesDialog';
import { SelectMenu } from '@/components/SelectMenu';
import { SettingsSidebar, MobileSettingsIndex, settingsNavItems, type SettingsTab } from '@/components/userSettings/SettingsNavigation';
import { platformOwnsIdentity } from '@/lib/platformIdentity';
import { useIsNarrowViewport } from '@/lib/useIsNarrowViewport';
import { useReportAccess } from '@/lib/useReportAccess';
import { PlatformModerationPanel } from '@/components/reports/PlatformModerationPanel';
import {
  Modal,
  TextField,
  Label,
  Input,
  TextArea,
  Alert,
} from '@heroui/react';
import type { UserStatus } from '@/types/api';
import { useShellHostsPresence } from '@/lib/shellPresence';

// OFFLINE is intentionally not selectable — the system flips users to
// OFFLINE automatically when their socket drops, and INVISIBLE already
// covers the "appear offline on purpose" case.
const STATUS_OPTIONS: UserStatus[] = ['ONLINE', 'IDLE', 'DND', 'INVISIBLE'];

export function UserSettingsModal(props: {
  open: boolean;
  onClose: () => void;
  /** Jump to this tab when the modal opens (e.g. 'profile', 'appearance'). */
  initialTab?: string;
}) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const updateCachedUser = useDataStore((s) => s.updateCachedUser);
  const guildIds = useDataStore((s) => s.guildOrder);
  const [avatarUrl, setAvatarUrl] = useState(me?.avatarUrl ?? '');
  // Bytes staged for upload. Null when the avatar was not touched this session
  // (or was cleared) — only a fresh pick produces bytes to send.
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);
  const [status, setStatus] = useState<UserStatus>(me?.status ?? 'ONLINE');
  const [bio, setBio] = useState(me?.bio ?? '');
  const [statusText, setStatusText] = useState(me?.statusText ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [tab, setTab] = useState<SettingsTab>('profile');
  const narrow = useIsNarrowViewport();
  const [mobileSectionOpen, setMobileSectionOpen] = useState(false);
  const [restoreIndexFocus, setRestoreIndexFocus] = useState(false);
  const backRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Closing with unsaved profile edits asks first (they live only here).
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // "Модерация Бункера" tab — shown only to platform moderators / root admins.
  const reportAccess = useReportAccess();
  // Owner's decision (21.10.2026): inside the platform shell (its Chat frame or the new
  // Desktop) the profile is the shell's — avatar, name, no status line or bio. «Профиль»
  // keeps only the status, and only until the shell's account menu says it shows it.
  const embedded = platformOwnsIdentity();
  // The shell's account menu shows the status once it says so (lib/shellPresence.ts).
  const shellHostsPresence = useShellHostsPresence();
  const showProfile = !embedded || !shellHostsPresence;
  const navItems = useMemo(
    () => settingsNavItems({ platformAdmin: reportAccess.platformAdmin, profile: showProfile }),
    [reportAccess.platformAdmin, showProfile],
  );
  const firstTab = navItems[0]?.key ?? 'profile';

  useEffect(() => {
    if (!props.open) { wasOpen.current = false; return; }
    if (wasOpen.current) return;
    wasOpen.current = true;
    setAvatarUrl(me?.avatarUrl ?? '');
    setAvatarBlob(null);
    setStatus(me?.status ?? 'ONLINE');
    setBio(me?.bio ?? '');
    setStatusText(me?.statusText ?? '');
    setBusy(false);
    setError(null);
    setConfirmDiscard(false);
    const requested = navItems.find(({ key }) => key === props.initialTab)?.key;
    setTab(requested ?? firstTab);
    setMobileSectionOpen(Boolean(requested));
    setRestoreIndexFocus(false);
  }, [props.open, props.initialTab, navItems, firstTab, me?.avatarUrl, me?.status, me?.bio, me?.statusText]);

  // Permission changes cannot leave a privileged section selected.
  const activeTab = navItems.some(({ key }) => key === tab) ? tab : firstTab;
  const showIndex = narrow && (!mobileSectionOpen || activeTab !== tab);
  useEffect(() => {
    if (props.open && narrow && mobileSectionOpen && !showIndex) backRef.current?.focus();
  }, [props.open, narrow, mobileSectionOpen, showIndex]);

  function pickSection(next: SettingsTab) {
    setTab(next);
    setMobileSectionOpen(true);
    setRestoreIndexFocus(false);
  }


  if (!me) return null;
  const currentUser = me;
  // "Сохранить" governs ONLY the profile fields (status-line / bio / avatar) —
  // status is instant. Disable Save until one of those changed, so the button
  // isn't a permanently-lit no-op. The name is not here: it's owned by
  // cispracs.ru and read-only in Bunker.
  const profileDirty =
    statusText.trim() !== (me.statusText ?? '').trim() ||
    bio.trim() !== (me.bio ?? '').trim() ||
    avatarUrl !== (me.avatarUrl ?? '') ||
    // Bytes staged but not yet uploaded are unsaved work too — without this a
    // re-pick of the same image reads as "nothing changed".
    avatarBlob !== null;
  function requestClose() {
    if (busy || uploading) return;
    if (profileDirty) setConfirmDiscard(true);
    else props.onClose();
  }
  const previewName = currentUser.displayName || currentUser.username;
  // Empty string in `avatarUrl` means "cleared" (different from initial
  // load where me?.avatarUrl is reflected). We fall through to me's avatar
  // only if avatarUrl matches it — otherwise empty means truly cleared.
  const previewAvatarUrl = avatarUrl || '';

  async function pickAvatar(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      // Still processed locally (resize / GIF dimension gate / re-encode) — the
      // result is both the preview and the bytes we upload on save.
      const dataUrl = await fileToIconDataUrl(file);
      setAvatarUrl(dataUrl);
      setAvatarBlob(dataUrlToBlob(dataUrl));
    } catch (err) {
      setUploadError(
        err instanceof ImageUploadError
          ? err.message
          : t('prof.settings.imageProcessFailed'),
      );
    } finally {
      setUploading(false);
    }
  }

  // Status applies INSTANTLY (like Discord/Telegram) — it's a quick toggle,
  // not part of the explicit profile "Сохранить". Optimistic + revert on
  // failure. PATCH /api/me with only `status` leaves the profile fields
  // untouched on the server.
  async function changeStatus(next: UserStatus) {
    if (next === status || busy) return;
    const prev = status;
    setStatus(next);
    setError(null);
    try {
      const nextUser = await api.updateMe({ status: next });
      setUser(nextUser);
      updateCachedUser(nextUser);
    } catch (err) {
      // Revert the optimistic toggle AND tell the user it didn't take —
      // otherwise the radio just snaps back with no explanation.
      setStatus(prev);
      const h = humanizeApiError(err, t('prof.settings.statusChangeFailed'));
      setError(h.hint ? `${h.title}. ${h.hint}` : h.title);
    }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    // A red avatar alert from the previous attempt would otherwise survive into
    // this one and look like a fresh failure.
    setUploadError(null);
    try {
      // Avatar first, as bytes. Its failure is reported on its own terms
      // (§4.7 codes) rather than as "profile save failed", because the two are
      // different problems for the user.
      if (avatarBlob) {
        try {
          await api.uploadAvatar(avatarBlob);
        } catch (err) {
          setBusy(false);
          setUploadError(avatarErrorMessage(err));
          return;
        }
      }
      const nextUser = await api.updateMe({
        // null-only: clearing stays a PATCH; supplying a URL is impossible by
        // type. Omitted entirely when the avatar was not touched.
        ...(!avatarBlob && !avatarUrl.trim() && currentUser.avatarUrl ? { avatarUrl: null } : {}),
        bio: bio.trim() ? bio.trim() : null,
        statusText: statusText.trim() ? statusText.trim() : null,
      });
      setUser(nextUser);
      updateCachedUser(nextUser);
      // The server echoes user:update to every guild; patch the rosters now.
      for (const guildId of guildIds) rosterCache.userUpdated(guildId, nextUser);
      props.onClose();
    } catch (err) {
      const h = humanizeApiError(err, t('prof.settings.saveProfileFailed'));
      setError(h.hint ? `${h.title}. ${h.hint}` : h.title);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    <Modal.Backdrop
      className="app-viewport-overlay"
      isOpen={props.open}
      // Block close during save so the user doesn't lose state to a stray
      // Esc or backdrop click — both are gated while busy.
      isDismissable={!busy && !uploading}
      isKeyboardDismissDisabled={busy || uploading}
      onOpenChange={(open) => {
        if (!open) requestClose();
      }}
    >
      <Modal.Container size="lg" scroll="inside" className="app-settings-container">
        <Modal.Dialog className="app-settings-dialog">
          {!busy && !uploading && narrow && <Modal.CloseTrigger aria-label={t('common.close')} />}
          <Modal.Header className="app-settings-header shrink-0">
            {!narrow && (
              <button type="button" className="app-workspace-close" onClick={requestClose}
                disabled={busy || uploading} aria-label={`${t('common.close')} ${t('prof.settings.title')}`}
                title={`${t('common.close')} ${t('prof.settings.title')}`}>
                <CloseIcon className="size-5" />
              </button>
            )}
            {narrow && !showIndex && (
              <button ref={backRef} type="button" aria-label={t('common.back')} className="app-settings-back"
                onClick={() => { setRestoreIndexFocus(true); setMobileSectionOpen(false); }}>
                <ChevronLeftIcon className="size-5" />
              </button>
            )}
            <Modal.Heading>{t('prof.settings.title')}</Modal.Heading>
          </Modal.Header>
          <Modal.Body className="app-settings-body">
        <div className="app-settings-layout flex min-h-0 min-w-0 flex-1 flex-col md:flex-row md:gap-5">
          {!narrow && <SettingsSidebar tab={activeTab} onPick={pickSection} items={navItems} />}
          <div key={showIndex ? 'index' : activeTab} className="app-settings-scroll scrollbar-thin min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain p-1.5 pr-2 pb-8">
          {showIndex ? <MobileSettingsIndex tab={activeTab} onPick={pickSection} items={navItems}
            userId={currentUser.id} name={previewName} avatarUrl={previewAvatarUrl} restoreFocus={restoreIndexFocus}
            showIdentity={!embedded} /> : (
          <>
          <header className="app-settings-page-head">
            <h1>{t(navItems.find(({ key }) => key === activeTab)?.labelKey ?? 'prof.settings.tab.profile')}</h1>
          </header>
          {activeTab === 'voice' ? (
            <VoicePreferencesTab />
          ) : activeTab === 'notifications' ? (
            <NotificationsTab />
          ) : activeTab === 'appearance' ? (
            <AppearanceTab />
          ) : activeTab === 'shortcuts' ? (
            <ShortcutsTab />
          ) : activeTab === 'privacy' ? (
            <PrivacyTab />
          ) : activeTab === 'moderation' ? (
            <PlatformModerationPanel rootAdmin={reportAccess.rootAdmin} />
          ) : (
          <form onSubmit={save} className="app-settings-profile flex flex-col">
            {/* The avatar itself opens the existing upload flow. */}
            <section className="app-settings-card app-settings-profile-panel">
              {!embedded && (
              <>
              <h2 className="app-settings-section-title">{t('prof.settings.yourAccount')}</h2>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => void pickAvatar(e)}
              />
              <div className="app-settings-profile-hero flex min-w-0 items-center gap-4">
                <div className="app-settings-profile-avatar shrink-0 self-start">
                  <Tooltip label={previewAvatarUrl ? t('prof.settings.changeAvatar') : t('prof.settings.uploadAvatar')}>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || busy}
                      aria-busy={uploading}
                      aria-label={previewAvatarUrl ? t('prof.settings.changeAvatar') : t('prof.settings.uploadAvatar')}
                      className="app-settings-avatar-upload group relative grid place-items-center overflow-hidden bg-accent/10 font-display text-3xl font-normal tracking-[-0.025em] text-accent transition-colors hover:bg-accent/15 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {previewAvatarUrl ? (
                        <img src={previewAvatarUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        previewName.charAt(0).toUpperCase()
                      )}
                      <span className="pointer-events-none absolute inset-0 grid place-items-center bg-black/55 text-[10px] font-medium text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100">
                        {uploading ? t('prof.settings.uploading') : t('prof.settings.change')}
                      </span>
                    </button>
                  </Tooltip>
                </div>
                <div className="app-settings-profile-copy min-w-0 flex-1">
                  <div>
                    <div className="break-words font-display text-2xl font-normal tracking-[-0.025em] text-ink">{previewName}</div>
                    <div className="break-all text-xs text-ink-muted">@{currentUser.username}</div>
                  </div>
                  <div className="app-settings-profile-links">
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || busy} className="app-settings-profile-link">
                      {t('prof.settings.changeAvatar')}
                    </button>
                    {previewAvatarUrl && (
                      <button type="button" onClick={() => { setAvatarUrl(''); setAvatarBlob(null); }}
                        disabled={uploading || busy} className="app-settings-profile-link">
                        {t('prof.settings.removeAvatar')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="app-settings-profile-footnote text-xs text-ink-muted">
                {uploadError ? (
                  <Alert status="danger">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>{uploadError}</Alert.Title>
                    </Alert.Content>
                  </Alert>
                ) : (
                  <p>{t('prof.settings.avatarHint')}</p>
                )}
                <a
                  href={getPlatformUrl() + '/profile'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-settings-profile-platform-link text-accent hover:text-accent-hover"
                  >
                  {t('prof.settings.displayNameOnPlatform')}
                </a>
              </div>
              </>
              )}
              {!embedded && <>
              <h2 className="app-settings-section-title">{t('prof.settings.aboutProfile')}</h2>
              <div className="app-settings-profile-fields">
                <TextField
                  className="app-settings-profile-field"
                  fullWidth
                  isInvalid={statusText.length >= LIMITS.user.statusTextMax}
                  value={statusText}
                  onChange={(v) => setStatusText(v.slice(0, LIMITS.user.statusTextMax))}
                >
                  <Label className="text-sm font-medium text-ink">{t('prof.settings.statusLine')}</Label>
                  <Input variant="secondary" maxLength={LIMITS.user.statusTextMax} placeholder={t('prof.settings.statusLinePlaceholder')} />
                </TextField>

                <TextField className="app-settings-profile-field" fullWidth isInvalid={bio.length >= LIMITS.user.bioMax}>
                  <Label className="text-sm font-medium text-ink">{t('prof.settings.profileBio')}</Label>
                  <TextArea
                    variant="secondary"
                    value={bio}
                    onChange={(e) => setBio(e.target.value.slice(0, LIMITS.user.bioMax))}
                    maxLength={LIMITS.user.bioMax}
                    rows={3}
                    placeholder={t('prof.settings.bioPlaceholder')}
                  />
                </TextField>
              </div>

              <div className="app-settings-profile-actions flex items-center justify-end gap-2" data-dirty={profileDirty}>
                <Button variant={profileDirty ? 'primary' : 'secondary'} type="submit" loading={busy} disabled={!profileDirty}>
                  {t('prof.settings.save')}
                </Button>
              </div>
              </>}

              {!shellHostsPresence && <>
              <h2 className="app-settings-section-title">{t('prof.settings.presence')}</h2>
              <div className="app-settings-profile-presence">
                <div className="app-settings-presence-copy">
                  <span className="app-settings-presence-label">{t('prof.settings.status')} <i aria-hidden="true" className={'size-2.5 rounded-full ' + presenceDotClass(status)} /></span>
                  {presenceDescription(status) && <p>{presenceDescription(status)}</p>}
                </div>
                <SelectMenu<UserStatus>
                  ariaLabel={t('prof.settings.status')}
                  value={status}
                  options={STATUS_OPTIONS.map((option) => ({
                    value: option,
                    label: presenceLabel(option),
                    icon: <span className={'size-2.5 shrink-0 rounded-full ' + presenceDotClass(option)} />,
                  }))}
                  onChange={(value) => void changeStatus(value)}
                  className="app-settings-status-select"
                />
              </div>
              </>}
            </section>


            {error && (
              <Alert status="danger">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>{error}</Alert.Title>
                </Alert.Content>
              </Alert>
            )}

            {!embedded && <ProfileQrCard username={currentUser.username} />}
          </form>
          )}
          </>
          )}
          </div>
        </div>
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
    <DiscardChangesDialog
      open={confirmDiscard}
      onKeep={() => setConfirmDiscard(false)}
      onDiscard={() => {
        setConfirmDiscard(false);
        props.onClose();
      }}
    />
    </>
  );
}
